/**
 * GuestsList — faithful port of the canonical guest list screen.
 * Table: Checkbox · Guest · Event · Company · Ticket · Status · Checked in · Source · Actions
 * Bulk bar: Email · SMS · Check in · Add to segment · Export · Remove
 */
import { useState, useMemo, useEffect, type ChangeEvent, type MouseEvent } from "react";
import { type Tone, StatusPill, Avatar, Btn, Card, Checkbox, FilterSelect, Icon, Input, Menu, MenuItem, MenuLabel, Pill, T, Tab, Tabs, Table, type TableColumn } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { EmptyState, ExplainerFooter, RowActions } from "../../shell/composites";
import { useViewMode, ViewToggle, CardActionBar, CARD_GRID, eventsSectionTabs } from "../../shell/list-composites";
import { can } from "../../shell/access";
import { toast } from "../../shell/toast";
import { listGuestSegments, listLocations, bulkExportGuests, bulkCheckInGuests, bulkRemoveGuests, drilldowns } from "@v0y4ge/api-client";
import { LOCATIONS_OPTS } from "../../shell/request-opts";
import { useNav } from "../../shell/router";
import { useGuests, type Guest } from "./useGuests";
import { useT, t as i18nT } from "../../lib/i18n";
import { friendlyDateTime } from "../../lib/datetime";

/* ── Status / Ticket pills ─────────────────────────────────────────── */
/* Guest status → canonical tone. Configuration, not data. */
const GUEST_TONE: Record<string, Tone> = { "checked-in": "success", registered: "info", waitlist: "warning", "no-show": "danger" };
function GuestStatus({ status }: { status: string }) {
  const t = useT();
  const label = status === "checked-in" ? t("guests.list.checkedIn", "Checked in")
    : status === "registered" ? t("guests.list.registered", "Registered")
    : status === "waitlist" ? t("guests.list.waitlist", "Waitlist")
    : status === "no-show" ? t("guests.list.noShow", "No-show") : status;
  return <StatusPill tone={GUEST_TONE[status] ?? "neutral"} dot={status === "checked-in"}>{label}</StatusPill>;
}
function TicketPill({ ticket }: { ticket: string }) {
  const t = useT();
  if (ticket === "VIP")   return <Pill tone="primary">{t("guests.list.vip", "VIP")}</Pill>;
  if (ticket === "Press") return <Pill tone="warn">{t("guests.list.press", "Press")}</Pill>;
  return <Pill>{t("guests.list.general", "General")}</Pill>;
}
/* Payments (2026-08-04) — a guest's payment state (paid / refunded / pending),
 * shown only for guests that carry a payment block; free/unpaid guests render
 * nothing so the list is visually unchanged for non-payment tenants. */
function PayPill({ payment }: { payment?: Guest["payment"] }) {
  const t = useT();
  if (!payment || !payment.status) return null;
  const s = payment.status;
  if (s === "refunded" || s === "partially_refunded") return <Pill tone="warn">{t("guests.list.refunded", "Refunded")}</Pill>;
  if (s === "paid") return <Pill tone="success">{t("guests.list.paid", "Paid")}</Pill>;
  if (s === "reserved" || s === "pending") return <Pill tone="info">{t("guests.list.paymentPending", "Payment pending")}</Pill>;
  if (s === "failed" || s === "disputed") return <Pill tone="danger">{t("guests.list.paymentFailed", "Payment issue")}</Pill>;
  return null;
}

/* ── Source cell ───────────────────────────────────────────────────── */
function SourceCell({ source }: { source?: string }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: T.space[2], fontSize: T.text.label.fontSize, color: T.soft }}>
      {source === "Salesforce" ? <span style={{ width: 8, height: 8, borderRadius: T.radius.pill, background: "#00A1E0", flexShrink: 0 }} /> :
       source === "Manual"     ? <Icon.edit size={10} /> :
                                  <Icon.globe size={10} />}
      {source || "—"}
    </span>
  );
}

/* ── PairFilter ────────────────────────────────────────────────────── */
// FilterSelect adapter for [value,label] option pairs whose first entry is the
// neutral "any" reset — keeps the guest filters on the shared FilterSelect
// (component-adoption drain 2026-08-07; was a hand-rolled native <select>).
function PairFilter({ pairs, value, onChange }: { pairs: [string, string][]; value: string; onChange: (v: string) => void }) {
  const [anyVal, anyLabel] = pairs[0];
  const choices = pairs.slice(1);
  const active = value !== anyVal;
  const current = choices.find(([v]) => v === value)?.[1] ?? anyLabel;
  return (
    <FilterSelect
      label={active ? current : anyLabel}
      anyLabel={anyLabel}
      active={active}
      options={choices.map(([, l]) => l)}
      onPick={(picked) => { const hit = choices.find(([, l]) => l === picked); onChange(hit ? hit[0] : anyVal); }}
      onClear={() => onChange(anyVal)}
    />
  );
}

const FILTER_FIELDS = [ // ui-config — option lists resolved at render (company from the served guest rows, addendum item 6; location from listLocations)
  { id: "company",    label: "Company",     options: [] as string[] }, // derived from guests — the tenant's real company set
  { id: "segment",    label: "Segment",     options: ["VIP","Press list","Early bird"] },
  { id: "ticketType", label: "Ticket type", options: ["VIP","General","Press"] },
  { id: "location",   label: "Location",    options: [] }, // populated from listLocations
];

/* ── Tabs ─────────────────────────────────────────────────────────── */
/** Secondary subtab row beneath the primary Events tabs, mirroring the pattern
 *  the ticket screens already use (TicketsSubTabs). */
export function GuestsSubTabs({ nav, active, tabs }: { nav: ReturnType<typeof useNav>; active: string; tabs: Array<{ id: string; label: string; count?: number; onClick: () => void }> }) {
  return (
    <Tabs value={active} onChange={() => { /* each tab owns its own nav.onClick */ }} variant="pill" aria-label={i18nT("guests.subtabs.ariaLabel", "Guest subsections")} style={{ padding: "12px 24px 0", background: T.canvas, flexShrink: 0 }}>
      {tabs.map((tb) => (
        <Tab key={tb.id} value={tb.id} active={tb.id === active} count={tb.count} onClick={tb.onClick}>{tb.label}</Tab>
      ))}
    </Tabs>
  );
}

function guestTabs(nav: ReturnType<typeof useNav>, totalGuests: number, waitlistCount: number, segmentsCount?: number) {
  return [
    { id: "guests",        label: i18nT("guests.list.allGuests", "All guests"),    count: totalGuests,   onClick: () => nav.go("guests") },
    { id: "segments",      label: i18nT("guests.list.segments", "Segments"),       count: segmentsCount, onClick: () => nav.go("segments") },
    { id: "registrations", label: i18nT("guests.list.registrations", "Registrations"),                        onClick: () => nav.go("registrations.today") },
    { id: "waitlist",      label: i18nT("guests.list.waitlist", "Waitlist"),       count: waitlistCount, onClick: () => nav.go("waitlist") },
    { id: "checkin",       label: i18nT("guests.list.checkInadc71958", "Check-in"),                             onClick: () => nav.go("checkins.today") },
  ];
}

/* ═══ Main screen ════════════════════════════════════════════════════ */
export default function GuestsList() {
  const t = useT();
  const [locationOptions, setLocationOptions] = useState<string[]>([]);
  useEffect(() => {
    listLocations({}, LOCATIONS_OPTS).then((r: any) => {
      const arr = r.locations ?? r.data ?? [];
      setLocationOptions(arr.map((l: any) => l.name ?? l.locationName ?? ""));
    }).catch(() => {});
  }, []);
  const nav = useNav();
  const eventId = nav.params?.eventId as string | undefined;
  // §2.3 (2026-07-26): consume the hook's REAL server total and page past the
  // 200-row default — the old footer read both numbers off the truncated
  // client array and shipped inert hardcoded page buttons.
  const [limit, setLimit] = useState(200);
  const { guests, loading, reload, total } = useGuests({ eventId, limit });

  // Real Segments tab total — GuestSegmentListResponse.pagination.total,
  // threaded into guestTabs like totalGuests/waitlistCount. No badge until it resolves.
  const [segmentsTotal, setSegmentsTotal] = useState<number | undefined>(undefined);
  useEffect(() => {
    let live = true;
    listGuestSegments()
      .then((r) => { if (live) setSegmentsTotal(r.pagination?.total ?? r.segments?.length); })
      .catch(() => { /* keep badge absent — never fabricate */ });
    return () => { live = false; };
  }, []);

  const [query, setQuery]         = useState("");
  const [statusF, setStatusF]     = useState("any");
  const [ticketF, setTicketF]     = useState("any");
  const [sourceF, setSourceF]     = useState("any");
  const [customFilters, setCustomF] = useState<{ id: string; fieldId: string; value: string }[]>([]);
  const [view, setView]           = useViewMode("guests:view");
  const [selected, setSelected]   = useState(new Set<string>());
  const [exporting, setExporting] = useState(false);

  const canWrite  = can("guests.write");
  const canExport = can("reports.export");
  const canRefund = can("billing.write"); // Payments (2026-08-04) — gates the Refund row action

  const addFilter = (fieldId: string) => {
    const f = FILTER_FIELDS.find((x) => x.id === fieldId);
    if (!f) return;
    setCustomF((arr) => [...arr, { id: `cf${Date.now()}`, fieldId, value: "any" }]);
  };
  const updateCustom = (id: string, value: string) => setCustomF((arr) => arr.map((f) => f.id === id ? { ...f, value } : f));
  const removeCustom = (id: string) => setCustomF((arr) => arr.filter((f) => f.id !== id));

  const rows = useMemo(() => guests.filter((g) => {
    if (statusF !== "any" && g.status !== statusF) return false;
    if (ticketF !== "any" && g.ticket !== ticketF) return false;
    if (sourceF !== "any" && g.source !== sourceF) return false;
    if (query.trim()) {
      const hay = [g.name, g.email, g.company, g.ticket].filter(Boolean).join(" ").toLowerCase();
      if (!hay.includes(query.toLowerCase())) return false;
    }
    for (const cf of customFilters) {
      if (cf.value === "any") continue;
      const f = FILTER_FIELDS.find((x) => x.id === cf.fieldId);
      if (!f) continue;
      if (cf.fieldId === "company"    && g.company !== cf.value) return false;
      if (cf.fieldId === "segment"    && !(g.segments || []).includes(cf.value)) return false;
      if (cf.fieldId === "ticketType" && g.ticket !== cf.value) return false;
    }
    return true;
  }), [guests, query, statusF, ticketF, sourceF, customFilters]);

  const checkedIn = guests.filter((g) => g.status === "checked-in").length;
  const waitlist  = guests.filter((g) => g.status === "waitlist").length;
  // ui-local — is any client-side filter narrowing the LOADED rows? Three counts
  // are in play and must not be conflated: `total` (server total for the query),
  // `guests.length` (loaded so far, grows with Load more), `rows.length` (loaded
  // rows surviving the filters). Only the footer used `total` before, so the tab
  // badge and the counter below both reported the first page as the population.
  const filtersActive = !!(query || statusF !== "any" || ticketF !== "any" || sourceF !== "any" || customFilters.length > 0);

  const allSel  = rows.length > 0 && rows.every((g) => selected.has(g.id));
  const someSel = selected.size > 0 && !allSel;
  const toggleSel = (id: string) => { const s = new Set(selected); s.has(id) ? s.delete(id) : s.add(id); setSelected(s); };
  const selectAll = () => setSelected(new Set(rows.map((g) => g.id)));
  const clearAll  = () => setSelected(new Set());

  const downloadBlob = (filename: string, content: string, mime: string) => {
    const url = URL.createObjectURL(new Blob([content], { type: mime }));
    Object.assign(document.createElement("a"), { href: url, download: filename }).click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  // Explicit-selection export (single guest, bulk bar, or header-with-selection)
  // via the real bulkExportGuests op — downloaded rows always match exactly
  // what's checked, never a client-only re-serialization of loaded rows.
  const exportSelected = async (scope: Guest[], label: string) => {
    if (exporting || scope.length === 0) return;
    setExporting(true);
    try {
      const res = await bulkExportGuests({ guestIds: scope.map((g) => g.id), format: "csv" });
      const filename = res.job.filename || `${label.replace(/[^\w-]+/g, "-")}.csv`;
      if (res.csv) downloadBlob(filename, res.csv, "text/csv");
      else if (res.job.downloadUrl) Object.assign(document.createElement("a"), { href: res.job.downloadUrl, download: filename, target: "_blank", rel: "noopener" }).click();
      const n = res.job.rowCount ?? scope.length;
      toast({ tone: "success", title: t("guests.list.exportedNGuestN1S.icu", { count: n }), subtitle: label });
    } catch (e) {
      toast({ tone: "danger", title: t("guests.list.exportFailed", "Export failed"), subtitle: e instanceof Error ? e.message : t("guests.list.pleaseTryAgain", "Please try again.") });
    } finally {
      setExporting(false);
    }
  };

  // Header "Export" with nothing selected means "every guest" — the real
  // global exportGuests op (immediate inline CSV), not a re-dump of loaded rows.
  const exportAll = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const res = await drilldowns.exportGuests({ format: "csv" });
      downloadBlob("all-guests.csv", res.csvData, "text/csv");
      const n = res.total ?? guests.length;
      toast({ tone: "success", title: t("guests.list.exportedNGuestN1S.icu", { count: n }), subtitle: t("guests.list.allGuests", "All guests") });
    } catch (e) {
      toast({ tone: "danger", title: t("guests.list.exportFailed", "Export failed"), subtitle: e instanceof Error ? e.message : t("guests.list.pleaseTryAgain", "Please try again.") });
    } finally {
      setExporting(false);
    }
  };

  // Addendum item 6: row + bulk actions await the real guest bulk ops and
  // reconcile via reload; success only on ack. Resend-confirmation has no op
  // (REQ-085) and says so honestly.
  const [acting, setActing] = useState(false);
  const rowCheckIn = async (g: Guest) => {
    if (acting) return; setActing(true);
    try { await bulkCheckInGuests({ guestIds: [g.id] }); reload(); toast({ tone: "success", title: t("guests.list.checkedIn", "Checked in"), subtitle: g.name }); }
    catch (e) { toast({ tone: "danger", title: t("guests.list.couldnTCheckIn", "Couldn't check in"), subtitle: e instanceof Error ? e.message : String(e) }); }
    finally { setActing(false); }
  };
  const rowRemove = async (g: Guest) => {
    if (acting) return; setActing(true);
    try { await bulkRemoveGuests({ guestIds: [g.id] }); reload(); toast({ tone: "warn", title: t("guests.list.removedFromEvent", "Removed from event"), subtitle: g.name }); }
    catch (e) { toast({ tone: "danger", title: t("guests.list.couldnTRemove", "Couldn't remove"), subtitle: e instanceof Error ? e.message : String(e) }); }
    finally { setActing(false); }
  };
  const bulkCheckIn = async () => {
    const ids = [...selected]; if (!ids.length || acting) return; setActing(true);
    try { await bulkCheckInGuests({ guestIds: ids }); reload(); toast({ tone: "success", title: i18nT("guests.list.selectedSizeGuestsCheckedIn.icu", { value0: ids.length }) }); clearAll(); }
    catch (e) { toast({ tone: "danger", title: t("guests.list.couldnTCheckIn", "Couldn't check in"), subtitle: e instanceof Error ? e.message : String(e) }); }
    finally { setActing(false); }
  };
  const bulkRemove = async () => {
    const ids = [...selected]; if (!ids.length || acting) return; setActing(true);
    try { await bulkRemoveGuests({ guestIds: ids }); reload(); toast({ tone: "warn", title: i18nT("guests.list.selectedSizeGuestsRemoved.icu", { value0: ids.length }) }); clearAll(); }
    catch (e) { toast({ tone: "danger", title: t("guests.list.couldnTRemove", "Couldn't remove"), subtitle: e instanceof Error ? e.message : String(e) }); }
    finally { setActing(false); }
  };

  const mkActions = (g: Guest) => ({
    onView: () => nav.go("guests.profile", { id: g.id }),
    viewLabel: t("guests.list.viewProfile", "View profile"),
    onEdit: canWrite ? () => nav.go("guests.profile", { id: g.id, edit: 1 }) : undefined,
    moreHeader: g.name,
    moreItems: [
      { icon: <Icon.mail size={14} />, label: t("guests.list.sendMessage", "Send message"),          onClick: () => nav.go("guests.profile", { id: g.id, send: 1 }) },
      { icon: <Icon.send size={14} />, label: t("guests.list.resendConfirmation", "Resend confirmation"),   onClick: () => toast({ tone: "info", title: t("guests.list.resendNotWiredYet", "Resend isn't wired yet"), subtitle: t("guests.list.noResendConfirmationOpIsDeclared", "No resend-confirmation operation is declared (REQ-085) — nothing was sent.") }) },
      "divider" as const,
      { icon: <Icon.check size={14} />, label: g.status === "checked-in" ? t("guests.list.undoCheckIn", "Undo check-in") : t("guests.list.markCheckedIn", "Mark checked-in"), onClick: () => void rowCheckIn(g) },
      { icon: <Icon.ticket size={14} />, label: t("guests.list.changeTicketType", "Change ticket type"),  onClick: () => nav.go("guests.profile", { id: g.id, edit: 1 }) },
      { icon: <Icon.tags size={14} />,   label: t("guests.list.addToSegment", "Add to segment"),      onClick: () => nav.go("guests.profile", { id: g.id }) },
      // Payments (2026-08-04): refund/cancel is available only for a paid guest
      // and only to a billing-writer; it opens the guest's profile where the
      // refund flow (amount, reason, confirm) lives — the money op is never
      // fired blind from a menu click.
      ...(canRefund && g.payment?.paymentId && (g.payment.status === "paid" || g.payment.status === "partially_refunded") ? [
        "divider" as const,
        { icon: <Icon.refresh size={14} />, label: t("guests.list.refundPayment", "Refund payment"), sub: t("guests.list.opensTheRefundFlow", "Opens the refund flow"), onClick: () => nav.go("guests.profile", { id: g.id, refund: 1 }) },
      ] : []),
      "divider" as const,
      ...(canExport ? [{ icon: <Icon.download size={14} />, label: t("guests.list.exportThisGuest", "Export this guest"), onClick: () => void exportSelected([g], g.name) }] : []),
      ...(canWrite ? [{ icon: <Icon.trash size={14} />, label: t("guests.list.removeFromEvent", "Remove from event"), danger: true, onClick: () => void rowRemove(g) }] : []),
    ],
  });

  // Table-view columns (component-adoption drain 2026-08-07 — the hand-rolled
  // <table> is now the shared jrni-ui Table; selection/row-click/hover are the
  // primitive's). Checkbox column is Table's own; Actions stops row-nav.
  const columns: TableColumn<Guest>[] = [
    { id: "guest", header: t("guests.list.guest", "Guest"), cell: (g) => (
      <div style={{ display: "flex", gap: T.space[2], alignItems: "center" }}>
        <Avatar size={28} initials={(g.name.split(" ").map((p) => p[0]).join("").toUpperCase().slice(0, 2))} color={g.color ?? T.p1} />
        <div>
          <div style={{ color: T.n1, fontWeight: 600 }}>{g.name}</div>
          <div style={{ color: T.soft, fontSize: T.text.label.fontSize }}>{g.email}</div>
        </div>
      </div>
    ) },
    { id: "company", header: t("guests.list.company", "Company"), cell: (g) => <span style={{ color: T.n1 }}>{g.company || "—"}</span> },
    { id: "ticket", header: t("guests.list.ticket", "Ticket"), cell: (g) => <TicketPill ticket={g.ticket ?? ""} /> },
    { id: "status", header: t("guests.list.status", "Status"), cell: (g) => (
      <div style={{ display: "flex", gap: T.space[2], alignItems: "center", flexWrap: "wrap" }}>
        <GuestStatus status={g.status} /><PayPill payment={g.payment} />
      </div>
    ) },
    { id: "checked", header: t("guests.list.checkedIn", "Checked in"), cell: (g) => <span style={{ color: T.n1, fontVariantNumeric: "tabular-nums" }}>{g.checked ? friendlyDateTime(g.checked) : "—"}</span> },
    { id: "source", header: t("guests.list.source", "Source"), cell: (g) => <SourceCell source={g.source} /> },
    { id: "actions", header: t("guests.list.actions", "Actions"), minWidth: 140, cell: (g) => (
      <span style={{ display: "inline-flex" }} onClick={(e: MouseEvent<HTMLSpanElement>) => e.stopPropagation()}>
        <RowActions {...mkActions(g)} />
      </span>
    ) },
  ];

  return (
    /* The Guests CRM lost its rail item in the 2026-09-10 customers drop and
       now lives under the Events section's Attendees tab: the Events tab bar is
       the PRIMARY nav (Attendees active, so the tab that got you here is still
       lit), and this screen's own five tabs drop to a secondary row. */
    <Screen rail="events" tabs={eventsSectionTabs(nav, "attendees")} activeTab="attendees">
      <GuestsSubTabs nav={nav} active="guests" tabs={guestTabs(nav, total, waitlist, segmentsTotal)} />
      <PageHeader
        title={t("guests.list.guestsAndCustomers", "Guests and Customers")}
        subtitle={i18nT("guests.list.guestsLengthRegisteredCheckedinCheckedInWaitlistOnWaitlistLastUpdated12s.icu", { value0: guests.length, value1: checkedIn, value2: waitlist })}
        actions={<>
          <ViewToggle mode={view} onChange={setView} />
          {canWrite  ? <Btn variant="secondary" icon={<Icon.upload size={14} />} onClick={() => nav.go("guests.import")}>{t("common.actions.import", "Import")}</Btn> : null}
          {canExport ? <Btn variant="secondary" icon={<Icon.download size={14} />} disabled={exporting} onClick={() => void (selected.size > 0 ? exportSelected(rows.filter((g) => selected.has(g.id)), t("guests.list.selectedGuests", "Selected guests")) : exportAll())}>{t("common.actions.export", "Export")}</Btn> : null}
          {can("campaigns.send") ? <Btn variant="secondary" icon={<Icon.send size={14} />} onClick={() => nav.go("invite")}>{t("guests.list.sendInvite", "Send invite")}</Btn> : null}
          {canWrite  ? <Btn icon={<Icon.plus />} onClick={() => nav.go("guests.profile", { new: 1 })}>{t("guests.list.addGuest", "Add guest")}</Btn> : null}
        </>}
      />

      {/* Filter bar */}
      <div style={{ padding: `${T.space[3]} ${T.space[6]}`, display: "flex", gap: T.space[2], alignItems: "center", flexWrap: "wrap", borderBottom: `1px solid ${T.border}` }}>
        <Input full={false} value={query} onChange={(e: ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)} leading={<Icon.search size={14} />} placeholder={t("guests.list.searchByNameEmailCompany", "Search by name, email, company…")} style={{ width: 320 }} />
        <PairFilter value={statusF} onChange={setStatusF} pairs={[["any",t("guests.list.statusAny", "Status: any")],["registered","Registered"],["checked-in","Checked in"],["waitlist","Waitlist"],["no-show","No-show"]]} />
        <PairFilter value={ticketF} onChange={setTicketF} pairs={[["any",t("guests.list.ticketAny", "Ticket: any")],["VIP","VIP"],["General","General"],["Press","Press"]]} />
        <PairFilter value={sourceF} onChange={setSourceF} pairs={[["any",t("guests.list.sourceAny", "Source: any")],["Salesforce","Salesforce"],[t("guests.list.website", "Website"),"Website"],[t("guests.list.manual", "Manual"),"Manual"]]} />
        {customFilters.map((cf) => {
          const field = FILTER_FIELDS.find((f) => f.id === cf.fieldId);
          if (!field) return null;
          // Company options come from the SERVED guest rows — the tenant's real
          // company set (addendum item 6), never a baked list.
          const opts = field.id === "company" ? Array.from(new Set(guests.map((g) => g.company).filter(Boolean))).sort() as string[] : field.options;
          const anyLabel = `${field.label}: any`;
          // A custom filter is a FilterSelect the user can also drop entirely:
          // its inline Clear (×) / the "any" reset row removes the whole filter
          // (clearing a custom filter to "any" is equivalent to removing it).
          return (
            <FilterSelect key={cf.id} active
              label={cf.value !== "any" ? cf.value : anyLabel}
              anyLabel={anyLabel}
              options={opts}
              onPick={(v) => updateCustom(cf.id, v)}
              onClear={() => removeCustom(cf.id)} />
          );
        })}
        {(() => {
          const avail = FILTER_FIELDS.filter((f) => !customFilters.some((cf) => cf.fieldId === f.id));
          return avail.length ? (
            <Menu aria-label={t("guests.list.filterBy", "Filter by")}
              trigger={<Btn variant="ghost" size="sm" icon={<Icon.plus size={12} />}>{t("guests.list.addFilter", "Add filter")}</Btn>}>
              <MenuLabel>{t("guests.list.filterBy", "Filter by")}</MenuLabel>
              {avail.map((f) => <MenuItem key={f.id} onSelect={() => addFilter(f.id)}>{f.label}</MenuItem>)}
            </Menu>
          ) : null;
        })()}
        {(query || statusF !== "any" || ticketF !== "any" || sourceF !== "any" || customFilters.length > 0) ? (
          <Btn variant="ghost" size="sm" onClick={() => { setQuery(""); setStatusF("any"); setTicketF("any"); setSourceF("any"); setCustomF([]); }}>{t("guests.list.clearAll", "Clear all")}</Btn>
        ) : null}
        <span style={{ marginLeft: "auto", fontSize: T.text.bodySm.fontSize, color: T.soft }}>
          {selected.size > 0 ? <><strong style={{ color: T.n1 }}>{selected.size}</strong> {t("guests.list.selectedOf", "selected of")}{" "}{rows.length}</>
            : filtersActive ? <>{rows.length} {t("guests.list.of", "of")}{" "}{guests.length} {t("guests.list.loaded", "loaded")}</>
            : <>{guests.length} {t("guests.list.of", "of")}{" "}{total}</>}
        </span>
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 ? (
        <div style={{ margin: `0 ${T.space[6]} 0`, padding: `${T.space[2]} ${T.space[3]}`, background: T.p4, border: `1px solid ${T.p2}`, borderRadius: T.radius.control, display: "flex", alignItems: "center", gap: T.space[2], fontSize: T.text.bodySm.fontSize }}>
          <span onClick={allSel ? clearAll : selectAll} style={{ cursor: "pointer", display: "inline-flex" }}>
            <Checkbox checked={allSel} indeterminate={someSel} />
          </span>
          <span style={{ color: T.n1, fontWeight: 600 }}>{selected.size} {t("guests.list.selected", "selected")}</span>
          <span style={{ width: 1, height: 14, background: T.border }} />
          {can("campaigns.write") ? <Btn variant="ghost" size="sm" icon={<Icon.mail size={12} />} onClick={() => nav.go("campaigns.email")}>{t("guests.list.email", "Email")}</Btn> : null}
          {can("campaigns.write") ? <Btn variant="ghost" size="sm" icon={<Icon.msg size={12} />} onClick={() => nav.go("campaigns.sms")}>{t("guests.list.sms", "SMS")}</Btn> : null}
          {can("guests.checkin")  ? <Btn variant="ghost" size="sm" icon={<Icon.check size={12} />} disabled={acting} onClick={() => void bulkCheckIn()}>{t("guests.list.checkIn", "Check in")}</Btn> : null}
          {canWrite               ? <Btn variant="ghost" size="sm" icon={<Icon.tags size={12} />}>{t("guests.list.addToSegment", "Add to segment")}</Btn> : null}
          {canExport              ? <Btn variant="ghost" size="sm" icon={<Icon.download size={12} />} disabled={exporting} onClick={() => void exportSelected(rows.filter((g) => selected.has(g.id)), t("guests.list.selectedGuests", "Selected guests"))}>{t("common.actions.export", "Export")}</Btn> : null}
          {canWrite               ? <Btn variant="ghost" size="sm" danger icon={<Icon.trash size={12} />} disabled={acting} onClick={() => void bulkRemove()}>{t("common.actions.remove", "Remove")}</Btn> : null}
          <span style={{ marginLeft: "auto", display: "flex", gap: T.space[2] }}>
            {!allSel ? <Btn variant="ghost" size="sm" onClick={selectAll}>{t("guests.list.selectAll", "Select all")}{" "}{rows.length}</Btn> : null}
            <Btn variant="ghost" size="sm" icon={<Icon.x size={12} />} onClick={clearAll}>{t("guests.list.deselectAll", "Deselect all")}</Btn>
          </span>
        </div>
      ) : null}

      <div style={{ flex: 1, overflow: "auto", padding: `${T.space[3]} ${T.space[6]} ${T.space[6]}` }}>
        {rows.length === 0 ? (
          <EmptyState variant="card" icon={<Icon.users size={20} />} title={t("guests.list.noGuestsMatch", "No guests match")} body={t("guests.list.tryADifferentSearchOrFilter", "Try a different search or filter.")} action={canWrite ? <Btn icon={<Icon.plus size={14} />} onClick={() => nav.go("guests.profile", { new: 1 })}>{t("guests.list.addGuest", "Add guest")}</Btn> : null} />
        ) : view === "grid" ? (
          <div style={{ display: "grid", gridTemplateColumns: CARD_GRID, gap: T.space[4] }}>
            {rows.map((g) => {
              const isSel = selected.has(g.id);
              return (
                <Card key={g.id} style={{ padding: 0, display: "flex", flexDirection: "column", cursor: "pointer", overflow: "hidden", background: isSel ? T.p4 : T.white }}
                  onClick={() => nav.go("guests.profile", { id: g.id })}>
                  <div style={{ padding: `${T.space[4]} ${T.space[4]} ${T.space[4]}`, flex: 1, display: "flex", flexDirection: "column", gap: T.space[3] }}>
                    <div style={{ display: "flex", alignItems: "center", gap: T.space[2] }}>
                      <span onClick={(e) => { e.stopPropagation(); toggleSel(g.id); }} style={{ display: "inline-flex" }}>
                        <Checkbox checked={isSel} />
                      </span>
                      <Avatar size={36} initials={(g.name.split(" ").map((p) => p[0]).join("").toUpperCase().slice(0, 2))} color={g.color ?? T.p1} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: T.text.h3.fontSize, fontWeight: 700, color: T.n1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{g.name}</div>
                        <div style={{ fontSize: T.text.label.fontSize, color: T.soft, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{g.email}</div>
                      </div>
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: T.space[1] }}>
                      <GuestStatus status={g.status} /><TicketPill ticket={g.ticket ?? ""} /><PayPill payment={g.payment} />
                    </div>
                    <div style={{ marginTop: "auto", paddingTop: T.space[3], borderTop: `1px solid ${T.border}`, display: "flex", flexDirection: "column", gap: T.space[1], fontSize: T.text.label.fontSize, color: T.soft }}>
                      {g.company ? <span style={{ display: "inline-flex", alignItems: "center", gap: T.space[2] }}><Icon.tags size={11} /> {g.company}</span> : null}
                      <SourceCell source={g.source} />
                    </div>
                  </div>
                  <CardActionBar {...mkActions(g)} />
                </Card>
              );
            })}
          </div>
        ) : (
          <Card style={{ padding: 0 }}>
            <Table<Guest>
              columns={columns}
              rows={rows}
              getRowId={(g) => g.id}
              selectedIds={selected}
              onRowSelect={(id, sel) => setSelected((prev) => { const s = new Set(prev); if (sel) s.add(id); else s.delete(id); return s; })}
              onSelectAll={(sel) => (sel ? selectAll() : clearAll())}
              onRowClick={(g) => nav.go("guests.profile", { id: g.id })}
              style={{ border: "none", borderRadius: T.radius.none }}
            />
            <div style={{ padding: `${T.space[3]} ${T.space[4]}`, borderTop: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: T.text.bodySm.fontSize, color: T.soft }}>
              <span>{t("guests.list.showing1", "Showing 1–")}{" "}{rows.length} {t("guests.list.of", "of")}{" "}{total}</span>
              <div style={{ display: "flex", gap: T.space[1] }}>
                {guests.length < total ? (
                  <Btn variant="secondary" size="sm" onClick={() => setLimit((l) => l + 200)}>{t("guests.list.loadMore", "Load more")}</Btn>
                ) : null}
              </div>
            </div>
          </Card>
        )}
        <ExplainerFooter>
          <b>{t("guests.list.guests", "Guests")}</b> {t("guests.list.areCustomersRegisteredForOneOrMoreEventsTheirProfileCommsHistory", "are customers registered for one or more events. Their profile, comms history, and check-in state are shared across events.")}</ExplainerFooter>
      </div>
    </Screen>
  );
}
